import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, LoaderCircleIcon, SendHorizontalIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { NisoloSubmission, SizeTier } from '../hooks/useNisoloSubmissions';
import { NisoloOrderData } from '../hooks/useNisoloOrders';
import { LineOutcome, reconcileSubmission } from '../utils/submissionOutcome';
import { askCarli, AgentMessage } from '../utils/carliAgent';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const compact = (value: number) => Math.abs(value) >= 1000 ? `${value < 0 ? '−' : ''}$${Math.round(Math.abs(value) / 1000).toLocaleString('en-US')}K` : currency(value);
const perUnit = (value: number) => `$${value.toFixed(2)}`;
const count = (value: number) => value.toLocaleString('en-US');
const signedPct = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
export function UploadAIInsights({
  uploads,
  orders,
  loading,
  error,
  defaultOpen = false
}: {
  uploads: NisoloSubmission[];
  orders: NisoloOrderData | null;
  loading: boolean;
  error: string | null;
  defaultOpen?: boolean;
}) {
  // In the report dialog the single upload starts expanded.
  const [openUpload, setOpenUpload] = useState<string | null>(defaultOpen ? uploads[0]?.id ?? null : null);
  if (loading) {
    return <article className="flex items-center justify-center gap-2 rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-sm text-ink/55" data-id="element-2643" data-mp="3ifzm73knv89d">
        <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2644" data-mp="3nfch8blorln1" />
        Carli is reading the receiving file…
      </article>;
  }
  if (error) {
    return <article className="rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-center text-sm text-ink/55" data-id="element-2645" data-mp="b5ywj5l5x3k">{error}</article>;
  }
  if (uploads.length === 0) {
    return <article className="rounded-2xl border border-ink/10 bg-paper p-8 text-center" aria-labelledby="ai-insights-empty" data-id="element-2646" data-mp="1boqlh2k5gf8u">
        <h2 id="ai-insights-empty" className="text-base font-semibold" data-id="element-2647" data-mp="37rd4yob8isv4">AI insights</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-2648" data-mp="33rkv88a3ugo6">Select an inventory submission above to see Carli’s read on what she predicted and what actually happened.</p>
      </article>;
  }
  return <div className="space-y-4" data-id="element-2649" data-mp="3cs0vz9zgr9pc">
      {uploads.map(upload => <InsightReport key={upload.id} upload={upload} orders={orders} open={openUpload === upload.id || defaultOpen && uploads.length === 1} onToggle={() => setOpenUpload(current => current === upload.id ? null : upload.id)} data-id="element-2650" data-mp="2p15b6wtfderl" />)}
    </div>;
}
function InsightReport({
  upload,
  orders,
  open,
  onToggle
}: {
  upload: NisoloSubmission;
  orders: NisoloOrderData | null;
  open: boolean;
  onToggle: () => void;
}) {
  const outcome = useMemo(() => reconcileSubmission(upload, orders), [upload, orders]);
  const weightedMsrp = upload.received > 0 ? upload.msrp / upload.received : 0;
  const coreShare = upload.received > 0 ? Math.round(upload.sizes.filter(size => size.tier === 'Core').reduce((sum, size) => sum + size.units, 0) / upload.received * 100) : 0;
  const headlineTone = outcome.matched ? outcome.variance >= 0 ? 'text-positive' : 'text-[#94452E]' : 'text-ink';
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" data-id="element-2651" data-mp="bsuigs2hhngp">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-cream/45 sm:px-6" data-id="element-2652" data-mp="ziovpaqpte1h">
        <span className="flex min-w-0 items-center gap-3" data-id="element-2653" data-mp="vixyopali1oi">
          <CarliMark data-id="element-2654" data-mp="vhd44zoa6iqn" />
          <span className="min-w-0" data-id="element-2655" data-mp="vl0ujpyfip15">
            <span className="block text-base font-semibold text-ink" data-id="element-2656" data-mp="3ba2irietfw05">AI insights · {upload.name}</span>
            <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2657" data-mp="3gagimd69jzn3">
              {upload.grade} · {count(upload.received)} units · predicted {compact(upload.estimatedRecovery)}
              {outcome.matched && ` · actual ${compact(outcome.actualSales)} to date`}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3" data-id="element-2658" data-mp="3lu22w6h7qqaj">
          {outcome.matched ? <span className={`hidden items-center gap-1 text-xs font-semibold sm:flex ${headlineTone}`} data-id="element-2659" data-mp="3hsoze31lez6s">
              {outcome.variance >= 0 ? <TrendingUpIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2660" data-mp="iafunr2r32el" /> : <TrendingDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2661" data-mp="deifho38423e" />}
              {signedPct(outcome.variancePct)} vs predicted
            </span> : <span className="hidden rounded-full bg-ink/8 px-2.5 py-1 text-[11px] font-semibold text-ink/55 sm:inline" data-id="element-2662" data-mp="4d0y82e2xud4">Awaiting first sales</span>}
          <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2663" data-mp="2u3e5n5inu525" />
        </span>
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-6 sm:px-6" data-id="element-2664" data-mp="14migsxz5934m">
          <Scoreboard upload={upload} outcome={outcome} data-id="element-2665" data-mp="19j91jylw8mwd" />

          <CarliNarrative upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2666" data-mp="19ho1xukq2drn" />

          {outcome.matched && (outcome.overPerformers.length > 0 || outcome.underPerformers.length > 0) && <section className="mt-4 grid gap-3 lg:grid-cols-2" aria-label="Where the model was right and wrong" data-id="element-2667" data-mp="1nbyktva1fdps">
              <VarianceList title="Beat the prediction" tone="up" items={outcome.overPerformers} empty="Nothing has cleared above its estimate yet." data-id="element-2668" data-mp="1s9qlpesnua64" />
              <VarianceList title="Missed the prediction" tone="down" items={outcome.underPerformers} empty="Nothing has cleared below its estimate yet." data-id="element-2669" data-mp="21aoltlzz9xjf" />
            </section>}

          <RecoveryFactors upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2670" data-mp="1a7b89cp7559e" />

          <Composition upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2671" data-mp="15abbjse8leuy" />

          <ReportChat upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2672" data-mp="1jacd7a2bsqbh" />
        </div>}
    </article>;
}
function Scoreboard({
  upload,
  outcome
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
}) {
  const positive = outcome.variance >= 0;
  return <section aria-label="Predicted versus actual" data-id="element-2673" data-mp="1o5rn5qx7q22m">
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_0.8fr]" data-id="element-2674" data-mp="22x1pvvq1epa9">
        <article className="rounded-xl border border-ink/10 bg-cream/45 p-5" data-id="element-2675" data-mp="1xxp47eawjo99">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2676" data-mp="1xzry49f6e1j9">Predicted recovery</p>
          <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2677" data-mp="2bv3s2elibnz4">{currency(upload.estimatedRecovery)}</p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2678" data-mp="2qjnh681z6hfx">{perUnit(outcome.predictedAur)} per unit across {count(upload.received)} units</p>
        </article>

        <article className={`rounded-xl border p-5 ${outcome.matched ? positive ? 'border-positive/30 bg-positive/6' : 'border-[#BB5E40]/30 bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/45'}`} data-id="element-2679" data-mp="2vh5q3yfzswnx">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2680" data-mp="2lj960h52urdo">Actual recovery to date</p>
          <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${outcome.matched ? positive ? 'text-positive' : 'text-[#94452E]' : 'text-ink/40'}`} data-id="element-2681" data-mp="27rkuaeyqq4x8">
            {outcome.matched ? currency(outcome.actualSales) : '—'}
          </p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2682" data-mp="22tsznd6po4pc">
            {outcome.matched ? `${perUnit(outcome.actualAur)} per unit across ${count(outcome.soldUnits)} units sold` : 'No matched sales recorded against this upload yet'}
          </p>
        </article>

        <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2683" data-mp="29cfnoivtia60">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2684" data-mp="24ctbmcrq6zys">Variance on sold units</p>
          {outcome.matched ? <>
              <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-positive' : 'text-[#94452E]'}`} data-id="element-2685" data-mp="1zc59ow1ykyps">{signedPct(outcome.variancePct)}</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2686" data-mp="1qdbg7ejxcogg">{compact(outcome.variance)} against {currency(outcome.predictedForSold)} predicted for those units</p>
            </> : <>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink/40" data-id="element-2687" data-mp="1goa4w1obqv8t">—</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2688" data-mp="1bp6wc794y6w3">Variance appears once units from this upload sell</p>
            </>}
        </article>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-2689" data-mp="12nhadgy0w05p">
        <MiniStat label="Sell-through" value={`${outcome.sellThrough.toFixed(1)}%`} detail={`${count(outcome.soldUnits)} of ${count(upload.received)} units`} data-id="element-2690" data-mp="12nzbaqvmr2jy" />
        <MiniStat label="Units remaining" value={count(outcome.unsoldUnits)} detail={`${compact(outcome.remainingPredicted)} still forecast`} data-id="element-2691" data-mp="3j9e1u7uttxrc" />
        <MiniStat label="AUR vs forecast" value={outcome.matched ? signedPct(outcome.aurVariancePct) : '—'} detail={outcome.matched ? `${perUnit(outcome.actualAur)} actual vs ${perUnit(outcome.predictedAur)} predicted` : `${perUnit(outcome.predictedAur)} predicted`} tone={outcome.matched ? outcome.aurVariancePct >= 0 ? 'up' : 'down' : undefined} data-id="element-2692" data-mp="3ncafdk5if3of" />
      </div>

      <div className="mt-3" data-id="element-2693" data-mp="34iwts5nos9lh">
        <div className="flex items-center justify-between text-xs text-ink/55" data-id="element-2694" data-mp="39bg7d2mdj4qa">
          <span data-id="element-2695" data-mp="39dkfxxv7wrww">Recovered {compact(outcome.actualSales)}</span>
          <span data-id="element-2696" data-mp="2bvstuf3galgk">Forecast {compact(upload.estimatedRecovery)}</span>
        </div>
        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-ink/8" role="img" aria-label={`${Math.round(outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}% of forecast recovered`} data-id="element-2697" data-mp="2gudjxloznora">
          <div className="h-full rounded-full bg-[#BB5E40]" style={{
          width: `${Math.min(100, outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}%`
        }} data-id="element-2698" data-mp="2ogfypu10g09i" />
        </div>
      </div>
    </section>;
}
function MiniStat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'up' | 'down';
}) {
  return <article className="rounded-xl border border-ink/10 bg-paper px-4 py-3.5" data-id="element-2699" data-mp="25r9f84se74mn">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2700" data-mp="25p6r5qasvlkk">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${tone === 'up' ? 'text-positive' : tone === 'down' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2701" data-mp="2ao9kidhf43cp">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2702" data-mp="1i0vs8sonzvyx">{detail}</p>
    </article>;
}
function VarianceList({
  title,
  tone,
  items,
  empty
}: {
  title: string;
  tone: 'up' | 'down';
  items: LineOutcome[];
  empty: string;
}) {
  const up = tone === 'up';
  return <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2703" data-mp="1aifjrjsfrsk3">
      <div className="flex items-center gap-2" data-id="element-2704" data-mp="1ag345da5hua7">
        {up ? <TrendingUpIcon className="h-4 w-4 text-positive" aria-hidden="true" data-id="element-2705" data-mp="15i9x6i10ww8v" /> : <TrendingDownIcon className="h-4 w-4 text-[#94452E]" aria-hidden="true" data-id="element-2706" data-mp="10k8gg3r2l90k" />}
        <h4 className="text-sm font-semibold" data-id="element-2707" data-mp="10mcggalulnvk">{title}</h4>
      </div>
      {items.length === 0 ? <p className="mt-3 text-xs text-ink/50" data-id="element-2708" data-mp="wk7p5iivh3c3">{empty}</p> : <ul className="mt-3 space-y-2.5" data-id="element-2709" data-mp="rqdawum1dgke">
          {items.map(item => <li key={item.name} className="flex items-center justify-between gap-3 border-b border-ink/8 pb-2.5 last:border-0 last:pb-0" data-id="element-2710" data-mp="mtd4ujp6sdta">
              <span className="min-w-0" data-id="element-2711" data-mp="hrnrgxehro21">
                <span className="block truncate text-xs font-medium text-ink" data-id="element-2712" data-mp="cunkmk9tih9v">{item.name}</span>
                <span className="text-[11px] text-ink/50" data-id="element-2713" data-mp="8sk7lr7i34mh">
                  {count(item.soldUnits)} of {count(item.receivedUnits)} sold · {perUnit(item.actualAur)} actual vs {perUnit(item.predict)} predicted
                </span>
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${up ? 'text-positive' : 'text-[#94452E]'}`} data-id="element-2714" data-mp="34p175rdezow">
                {item.variance >= 0 ? '+' : '−'}{currency(Math.abs(item.variance))}
              </span>
            </li>)}
        </ul>}
    </article>;
}
function CarliNarrative({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSummary('');
    setFailed('');
    askCarli({
      messages: [{
        role: 'user',
        content: 'Give me your read on this upload: what you predicted, what actually happened, why, and what to do next.'
      }],
      context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Reply with exactly three lines, each starting with the label and a colon, and nothing else:', 'CALL: two sentences on what the model predicted for this lot and the reasoning behind that number.', 'RESULT: two sentences on what actually happened against that prediction, citing the specific styles, sizes, or price points responsible.', 'NEXT: two sentences on the single highest-value action to take now, stated concretely with the units or dollars involved.', 'Write the full sentences on the same line as the label. Do not add preamble, bullet points, markdown, or any other headings.']),
      maxTokens: 700
    }).then(reply => {
      if (!cancelled) setSummary(reply);
    }).catch((caught: Error) => {
      if (!cancelled) setFailed(caught.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [upload, outcome, weightedMsrp, coreShare]);
  const sections = useMemo(() => {
    if (!summary) return [];
    const order = ['CALL', 'RESULT', 'NEXT'];
    const positions = order.map(label => ({
      label,
      index: summary.search(new RegExp(`\\b${label}\\s*:`, 'i'))
    })).filter(entry => entry.index >= 0).sort((a, b) => a.index - b.index);
    return positions.map((entry, position) => {
      const next = positions[position + 1];
      const slice = summary.slice(entry.index, next ? next.index : undefined);
      return {
        label: entry.label,
        text: slice.replace(new RegExp(`^\\s*${entry.label}\\s*:\\s*`, 'i'), '').replace(/[*_#]/g, '').trim()
      };
    }).filter(section => section.text.length > 0);
  }, [summary]);
  const labels: Record<string, string> = {
    CALL: 'The call',
    RESULT: 'The result',
    NEXT: 'What to do next'
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5" aria-label="Carli’s read" data-id="element-2715" data-mp="1snugimsgrntt">
      <div className="flex items-center gap-2.5" data-id="element-2716" data-mp="3d6es09mh7x4d">
        <CarliMark data-id="element-2717" data-mp="3860fofdue10j" />
        <span data-id="element-2718" data-mp="385h89sltiv0r">
          <span className="block text-sm font-semibold" data-id="element-2719" data-mp="384z5se8kbkke">Carli’s read</span>
          <span className="block text-xs text-ink/50" data-id="element-2720" data-mp="343nf05dyctam">{upload.name} · {upload.grade}</span>
        </span>
      </div>

      {loading && <p className="mt-3 flex items-center gap-2 text-xs text-ink/55" data-id="element-2721" data-mp="2z6n9bvl0lsk1">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2722" data-mp="3vu9zjbrhwep1" />
          Comparing the forecast against actual sales…
        </p>}

      {failed && !loading && <p className="mt-3 text-xs leading-relaxed text-ink/60" data-id="element-2723" data-mp="3n1zmznt4nx4m">{failed}</p>}

      {sections.length > 0 && <dl className="mt-3 grid gap-4 lg:grid-cols-3" data-id="element-2724" data-mp="3i357ovudcqp9">
          {sections.map(section => <div key={section.label} data-id="element-2725" data-mp="jepwj5eo7999">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2726" data-mp="jfr8dc3ifzs1">{labels[section.label.toUpperCase()] ?? section.label}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-ink/75" data-id="element-2727" data-mp="xkhu7i4s87ap">{section.text}</dd>
            </div>)}
        </dl>}

      {!loading && !failed && sections.length === 0 && summary && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/75" data-id="element-2728" data-mp="oqmx8xdtdn65">{summary}</p>}
    </section>;
}
type Strength = 'Strong' | 'Moderate' | 'Limited';
const strengthTone: Record<Strength, string> = {
  Strong: 'bg-forest/12 text-forest',
  Moderate: 'bg-[#C9A04E]/20 text-[#8B6A20]',
  Limited: 'bg-[#BB5E40]/14 text-[#94452E]'
};
function RecoveryFactors({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  const topCategory = upload.categories[0];
  const categoryShare = upload.received > 0 && topCategory ? Math.round(topCategory.units / upload.received * 100) : 0;
  const deepLines = upload.lines.filter(line => line.units >= 100).length;
  const premiumUnits = upload.priceBands.filter(band => band.label === '$150 – $199' || band.label === '$200+').reduce((sum, band) => sum + band.units, 0);
  const premiumShare = upload.received > 0 ? Math.round(premiumUnits / upload.received * 100) : 0;
  const unsized = upload.sizes.find(size => size.label === 'UNSIZED' || size.label === 'Unsized')?.units ?? 0;
  const marketing = outcome.predictedAur * upload.received * 0.1;
  const dataGaps = upload.missingMsrp + upload.missingPredict + upload.missingUpc;
  const factors: Array<{
    title: string;
    strength: Strength;
    headline: string;
    points: string[];
    takeaway: string;
  }> = [{
    title: 'Pricing position',
    strength: outcome.matched ? outcome.aurVariancePct >= 0 ? 'Strong' : 'Moderate' : 'Moderate',
    headline: outcome.matched ? `Units are clearing at ${perUnit(outcome.actualAur)}, ${signedPct(outcome.aurVariancePct)} against the ${perUnit(outcome.predictedAur)} forecast.` : `The lot is modeled to clear at ${perUnit(outcome.predictedAur)}, ${recoveryRate}% of submitted MSRP.`,
    points: [`${perUnit(weightedMsrp)} weighted MSRP per unit`, `${perUnit(weightedMsrp * 0.7)} A-grade promotional price at ~30% off`, outcome.matched ? `${perUnit(outcome.actualAur)} realized on ${count(outcome.soldUnits)} units` : `${perUnit(outcome.predictedAur)} predicted recovery per unit`],
    takeaway: outcome.matched && outcome.aurVariancePct >= 0 ? 'Prices are holding above plan — hold the ladder rather than discounting.' : 'Price sits well below first-quality, which protects the brand but caps recovery.'
  }, {
    title: 'Product mix',
    strength: categoryShare >= 55 ? 'Limited' : categoryShare >= 35 ? 'Moderate' : 'Strong',
    headline: topCategory ? `${topCategory.label} carries ${categoryShare}% of units, setting the pace for the whole lot.` : 'Category mix is spread evenly across the assortment.',
    points: [`${count(upload.lineItems)} variant lines across ${count(upload.categories.length)} categories`, `${count(deepLines)} lines carry 100+ units`, upload.categories.slice(1, 3).map(item => `${item.label} ${count(item.units)}`).join(' · ') || 'No secondary category depth'],
    takeaway: categoryShare >= 55 ? 'Concentration means one category decides the outcome — merchandise it deliberately.' : 'A balanced mix gives multiple paths to recovery if one category stalls.'
  }, {
    title: 'Size distribution',
    strength: coreShare >= 55 ? 'Strong' : coreShare >= 35 ? 'Moderate' : 'Limited',
    headline: `${coreShare}% of units sit in core-demand sizes, which drive the fastest sell-through.`,
    points: [upload.sizes.filter(size => size.tier === 'Core').slice(0, 3).map(size => `${size.label} (${count(size.units)})`).join(' · ') || 'No core sizes identified', `${count(upload.sizes.filter(size => size.tier === 'Tail').reduce((sum, size) => sum + size.units, 0))} units in tail sizes`, unsized > 0 ? `${count(unsized)} units without a readable size` : 'Every unit carries a readable size'],
    takeaway: coreShare >= 50 ? 'The size curve favors liquidity — lead with core runs.' : 'Tail sizes need more time or markdown to clear.'
  }, {
    title: 'Price architecture',
    strength: premiumShare >= 45 ? 'Strong' : premiumShare >= 20 ? 'Moderate' : 'Limited',
    headline: `${premiumShare}% of units carry MSRP of $150 or higher, where recovery per unit is strongest.`,
    points: upload.priceBands.slice(0, 3).map(band => `${band.label}: ${count(band.units)} units`),
    takeaway: premiumShare >= 40 ? 'Higher retail price points give room to discount without eroding recovery.' : 'A lower-priced lot means volume, not price, has to carry recovery.'
  }, {
    title: 'Promotion potential',
    strength: marketing >= 20000 ? 'Strong' : marketing >= 5000 ? 'Moderate' : 'Limited',
    headline: `Projected recovery supports roughly ${compact(marketing)} of marketing at the standard 10% allocation.`,
    points: [`${currency(upload.estimatedRecovery)} projected gross recovery`, `${compact(marketing)} available for channel and content push`, outcome.matched ? `${count(outcome.unsoldUnits)} units still to place` : `${count(upload.received)} units awaiting first placement`],
    takeaway: 'Spend is best concentrated on the categories already converting.'
  }, {
    title: 'Data integrity',
    strength: dataGaps === 0 ? 'Strong' : dataGaps < upload.lineItems * 0.1 ? 'Moderate' : 'Limited',
    headline: dataGaps === 0 ? 'Every line carries MSRP, barcode, and a per-unit estimate.' : `${count(dataGaps)} field gaps across ${count(upload.lineItems)} lines widen the projection range.`,
    points: [`${count(upload.lineItems - upload.missingUpc)} of ${count(upload.lineItems)} lines have a barcode`, `${count(upload.lineItems - upload.missingMsrp)} of ${count(upload.lineItems)} lines have MSRP`, `${count(upload.shortShipped)} invoiced units never arrived`],
    takeaway: dataGaps === 0 ? 'Clean identifiers let us match sales back to this upload precisely.' : 'Gaps mean some sales cannot be attributed back to this lot.'
  }];
  return <section className="mt-4" aria-label="Recovery factors" data-id="element-2729" data-mp="3uyez55l9lqr5">
      <h3 className="text-sm font-semibold" data-id="element-2730" data-mp="3uzzugxfeldq2">What is driving this lot</h3>
      <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2731" data-mp="3q6euizu929o8">
        {factors.map(factor => <article key={factor.title} className="flex flex-col rounded-xl border border-ink/10 bg-paper p-4" data-id="element-2732" data-mp="32gb0nk6v5oaf">
            <div className="flex items-start justify-between gap-2" data-id="element-2733" data-mp="2xhzq178qv9r5">
              <h4 className="text-sm font-semibold" data-id="element-2734" data-mp="1d08d3y3jejnn">{factor.title}</h4>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${strengthTone[factor.strength]}`} data-id="element-2735" data-mp="1hr70tiquuk0x">{factor.strength}</span>
            </div>
            <p className="mt-2 text-xs font-semibold leading-relaxed text-ink/80" data-id="element-2736" data-mp="141mzmhcx9utq">{factor.headline}</p>
            <ul className="mt-2.5 space-y-1.5" data-id="element-2737" data-mp="17zayfvhqk7qw">
              {factor.points.filter(Boolean).map(point => <li key={point} className="flex gap-2 text-xs leading-relaxed text-ink/60" data-id="element-2738" data-mp="1vp5boe5yvybo">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#BB5E40]" aria-hidden="true" data-id="element-2739" data-mp="20nym1xpyj97m" />
                  {point}
                </li>)}
            </ul>
            <p className="mt-auto border-t border-ink/10 pt-2.5 text-xs leading-relaxed text-ink/55" style={{
          marginTop: '0.875rem'
        }} data-id="element-2740" data-mp="1rnjrs8v0mjrz">{factor.takeaway}</p>
          </article>)}
      </div>
    </section>;
}
function Composition({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [open, setOpen] = useState(false);
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  return <section className="mt-4 overflow-hidden rounded-xl border border-ink/10 bg-paper" aria-label="Assumptions and composition" data-id="element-2741" data-mp="ht97yeuc582w">
      <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-cream/45" data-id="element-2742" data-mp="1ehxloyz0q96i">
        <span data-id="element-2743" data-mp="10jh3aarsmu8j">
          <span className="block text-sm font-semibold text-ink" data-id="element-2744" data-mp="10hw8ql5hbbao">How the forecast was built</span>
          <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2745" data-mp="3q8jcrupm0red">
            {perUnit(weightedMsrp)} weighted MSRP · {recoveryRate}% modeled recovery · {coreShare}% core sizes · {count(upload.lineItems)} variant lines
          </span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2746" data-mp="20p1etj3k11wi" />
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-5" data-id="element-2747" data-mp="29qj15j7k1ztn">
          <ol className="grid gap-3 lg:grid-cols-4" data-id="element-2748" data-mp="2eisn6o5jufqn">
            <WaterfallStep label="Weighted MSRP" value={perUnit(weightedMsrp)} detail="Retail value per unit across the lot." data-id="element-2749" data-mp="2ekn6xz5341t5" />
            <WaterfallStep label="A-grade promo price" value={perUnit(weightedMsrp * 0.7)} detail="Nisolo was running ~30% off new product." data-id="element-2750" data-mp="2oggk9kqmnzbz" />
            <WaterfallStep label="Predicted recovery" value={perUnit(outcome.predictedAur)} detail={`${recoveryRate}% of MSRP after condition and demand.`} data-id="element-2751" data-mp="2tgbadm2kwkvz" />
            <WaterfallStep label="Actual recovery" value={outcome.matched ? perUnit(outcome.actualAur) : '—'} detail={outcome.matched ? `${signedPct(outcome.aurVariancePct)} against the forecast.` : 'Pending first matched sales.'} highlight data-id="element-2752" data-mp="z2acb3eus1wy" />
          </ol>

          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-2753" data-mp="21g87n9ka30oh">
            <BreakdownStat label="Variant lines" value={count(upload.lineItems)} detail={`${count(upload.received)} units received`} data-id="element-2754" data-mp="2fezfwfcgls4p" />
            <BreakdownStat label="Units per line" value={upload.lineItems > 0 ? (upload.received / upload.lineItems).toFixed(1) : '—'} detail={`${count(upload.lines.filter(line => line.units >= 100).length)} lines carry 100+ units`} data-id="element-2755" data-mp="2bcb727nqwl7q" />
            <BreakdownStat label="Modeled recovery" value={`${recoveryRate}%`} detail={`of ${currency(upload.msrp)} submitted MSRP`} data-id="element-2756" data-mp="2kbycd4jlib62" />
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2757" data-mp="2u3lycpxdv005">
            <Distribution title="Sizes" items={upload.sizes.slice(0, 8)} total={upload.received} tiered data-id="element-2758" data-mp="2u684qm8jf9gs" />
            <Distribution title="Categories" items={upload.categories.slice(0, 8)} total={upload.received} data-id="element-2759" data-mp="334k3xcngzfku" />
            <Distribution title="MSRP bands" items={upload.priceBands} total={upload.received} data-id="element-2760" data-mp="3mqln5qdaqjpl" />
          </div>

          <div className="mt-4 grid gap-4 border-t border-ink/10 pt-4 lg:grid-cols-2" data-id="element-2761" data-mp="3rpyjr2tyz55d">
            <div data-id="element-2762" data-mp="3dpxi3l5vrtou">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2763" data-mp="3dqfau7tc3ps0">Confidence: medium — what we knew</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2764" data-mp="3hpo6xjbujzqq">
                Unit counts, MSRP, size mix, and the {upload.grade} designation were known. {count(upload.lineItems - upload.missingPredict)} of {count(upload.lineItems)} lines carried a per-unit estimate, and {count(upload.shortShipped)} invoiced units never arrived.
              </p>
            </div>
            <div data-id="element-2765" data-mp="2z1szeh6tvi62">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2766" data-mp="2z1asmqmjhljw">What we had to estimate</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2767" data-mp="2l331izc6pegn">
                Demand by style, liquidity by size, and how consistently the {upload.grade} condition would land with customers — the network had no prior Nisolo sell-through to calibrate against.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}
function WaterfallStep({
  label,
  value,
  detail,
  highlight
}: {
  label: string;
  value: string;
  detail: string;
  highlight?: boolean;
}) {
  return <li className={`rounded-xl border p-4 ${highlight ? 'border-[#BB5E40] bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/40'}`} data-id="element-2768" data-mp="2rkec1di1mxt0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2769" data-mp="2hss01exvsup7">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${highlight ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2770" data-mp="2ct5od9xpbmii">{value}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-ink/55" data-id="element-2771" data-mp="27vdkrinp2byh">{detail}</p>
    </li>;
}
function BreakdownStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-xl border border-ink/10 bg-cream/40 px-4 py-3.5" data-id="element-2772" data-mp="1yuyxidr66msc">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2773" data-mp="ecem0qx5qdhs">{label}</p>
      <p className="mt-1.5 font-display text-xl font-semibold tracking-tight text-ink" data-id="element-2774" data-mp="j9x00vq17iwn">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2775" data-mp="nc1qxmp3i1fl">{detail}</p>
    </article>;
}
function Distribution({
  title,
  items,
  total,
  tiered
}: {
  title: string;
  items: Array<{
    label: string;
    units: number;
    tier?: SizeTier;
  }>;
  total: number;
  tiered?: boolean;
}) {
  const max = Math.max(...items.map(item => item.units), 1);
  const tone: Record<SizeTier, string> = {
    Core: 'bg-[#BB5E40]',
    Adjacent: 'bg-[#C9A04E]',
    Tail: 'bg-ink/25'
  };
  return <article data-id="element-2776" data-mp="4khb7la29njl">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2777" data-mp="4jpf89cjxnss">{title}</p>
      <ul className="mt-2.5 space-y-1.5" data-id="element-2778" data-mp="9elik5n8nxl7">
        {items.map(item => <li key={item.label} className="flex items-center gap-2.5" data-id="element-2779" data-mp="381r13meusz1y">
            <span className="w-20 shrink-0 truncate text-xs text-ink/65" data-id="element-2780" data-mp="3d0tz4mwf250f">{item.label}</span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-2781" data-mp="2mr6kds4uvmtg">
              <span className={`block h-full rounded-full ${tiered ? tone[item.tier ?? 'Tail'] : 'bg-[#BB5E40]'}`} style={{
            width: `${Math.max(2, item.units / max * 100)}%`
          }} data-id="element-2782" data-mp="2rqsz660c1b4d" />
            </span>
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-ink/55" data-id="element-2783" data-mp="2iq326yzc5ep2">{total > 0 ? Math.round(item.units / total * 100) : 0}%</span>
          </li>)}
      </ul>
    </article>;
}
function buildContext(upload: NisoloSubmission, outcome: ReturnType<typeof reconcileSubmission>, weightedMsrp: number, coreShare: number, instructions: string[]) {
  return [`Inventory submission: "${upload.name}" (${upload.grade}), uploaded ${upload.arrived}, brand Nisolo, sold through the Manymoons recovery network.`, `Received ${upload.received} units across ${upload.lineItems} variant lines. ${upload.shortShipped} invoiced units never arrived.`, `Submitted MSRP value ${currency(upload.msrp)} (${perUnit(weightedMsrp)} weighted per unit). Nisolo A-grade was promoting at ~30% off, so first-quality price sat near ${perUnit(weightedMsrp * 0.7)}.`, `FORECAST: ${currency(upload.estimatedRecovery)} total, ${perUnit(outcome.predictedAur)} per unit received.`, outcome.matched ? `ACTUAL SO FAR: ${outcome.soldUnits} units sold for ${currency(outcome.actualSales)} (${perUnit(outcome.actualAur)} AUR). Those same units were forecast at ${currency(outcome.predictedForSold)}, so variance is ${currency(outcome.variance)} (${signedPct(outcome.variancePct)}). AUR variance ${signedPct(outcome.aurVariancePct)}. Sell-through ${outcome.sellThrough.toFixed(1)}%. ${outcome.unsoldUnits} units remain, carrying ${currency(outcome.remainingPredicted)} of remaining forecast.` : 'ACTUAL SO FAR: no sales have been matched to this upload yet, so there is no realized variance.', outcome.overPerformers.length > 0 ? `Beat forecast: ${outcome.overPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', outcome.underPerformers.length > 0 ? `Missed forecast: ${outcome.underPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', `Composition: ${coreShare}% of units in core-demand sizes. Categories: ${upload.categories.slice(0, 6).map(item => `${item.label} (${item.units})`).join(', ')}. MSRP bands: ${upload.priceBands.map(item => `${item.label} (${item.units})`).join(', ')}.`, '', ...instructions].filter(Boolean).join('\n');
}
function ReportChat({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [messages, setMessages] = useState<Array<{
    id: number;
    role: 'user' | 'assistant';
    text: string;
  }>>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const suggestions = outcome.matched ? ['Why did the forecast miss?', 'What should we reprice?', 'How should we clear what’s left?'] : ['What should launch first?', 'How should we price this?', 'What are the biggest risks?'];
  const send = async (question: string) => {
    if (!question.trim() || thinking) return;
    const history: AgentMessage[] = [...messages.map(message => ({
      role: message.role,
      content: message.text
    })), {
      role: 'user',
      content: question
    }];
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'user',
      text: question
    }]);
    setDraft('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Answer only from this submission data. Be specific and commercial. Two short paragraphs maximum.']),
        maxTokens: 600
      });
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: reply
      }]);
    } catch (caught) {
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void send(draft.trim());
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/30 bg-[#BB5E40]/5 p-4 sm:p-5" aria-label="Ask Carli about this report" data-id="element-2784" data-mp="wnzf4byj08fu">
      <div className="flex items-center gap-2.5" data-id="element-2785" data-mp="msfh9k0eizog">
        <CarliMark data-id="element-2786" data-mp="mtqm7n5l2dh5" />
        <span data-id="element-2787" data-mp="rqqvs81n5yd0">
          <span className="block text-sm font-semibold" data-id="element-2788" data-mp="rq7k392l467f">Ask Carli about this upload</span>
          <span className="block text-xs text-ink/55" data-id="element-2789" data-mp="37i4vpvx70jgm">She answers from this submission’s units, pricing, and realized sales.</span>
        </span>
      </div>

      {messages.length > 0 && <ul className="mt-4 max-h-56 space-y-2.5 overflow-y-auto pr-1" style={{
      scrollbarGutter: 'stable'
    }} aria-live="polite" data-id="element-2790" data-mp="34xfdchi50mqs">
          {messages.map(message => <li key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex gap-2.5'} data-id="element-2791" data-mp="300gifwqngbrj">
              {message.role === 'assistant' && <CarliMark compact data-id="element-2792" data-mp="38yirmzkjeapw" />}
              <p className={`max-w-2xl whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/80'}`} data-id="element-2793" data-mp="38zk2b2xn4v73">{message.text}</p>
            </li>)}
        </ul>}

      {thinking && <p className="mt-3 flex items-center gap-2 text-xs text-[#94452E]" data-id="element-2794" data-mp="2v0axezevxnif">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-2795" data-mp="2q8102ybfxl64" />
          Carli is reviewing this upload…
        </p>}

      <form onSubmit={submit} className="mt-3 flex items-center gap-2 rounded-xl border border-[#BB5E40]/30 bg-paper p-1.5 pl-3.5 focus-within:border-[#BB5E40] focus-within:ring-2 focus-within:ring-[#BB5E40]/15" data-id="element-2796" data-mp="22hdn3ymo45cp">
        <label htmlFor={`report-chat-${upload.id}`} className="sr-only" data-id="element-2797" data-mp="1emnwp2i1119">Ask Carli about {upload.name}</label>
        <input id={`report-chat-${upload.id}`} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli about this upload…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-2798" data-mp="6dh2tfxci5g3" />
        <button type="submit" disabled={!draft.trim() || thinking} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" aria-label="Send question" data-id="element-2799" data-mp="3jmc3oiank8o4">
          {thinking ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-2800" data-mp="13u63h6ak71nw" /> : <SendHorizontalIcon className="h-4 w-4" data-id="element-2801" data-mp="17wsyfg2j12pb" />}
        </button>
      </form>

      {messages.length === 0 && <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-2802" data-mp="2sellcc1d0ej3">
          {suggestions.map(item => <button key={item} type="button" onClick={() => void send(item)} className="rounded-full border border-[#BB5E40]/35 bg-paper px-3 py-1.5 text-xs font-medium text-[#94452E] transition-colors hover:bg-[#BB5E40] hover:text-cream" data-id="element-2803" data-mp="2odiay522zap9">
              {item}
            </button>)}
        </div>}
    </section>;
}
function CarliMark({
  compact = false
}: {
  compact?: boolean;
}) {
  return <span aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/12 ${compact ? 'h-7 w-7' : 'h-9 w-9'}`} data-id="element-2804" data-mp="24rlw1uvcoqwh">
      <span className="bg-[#BB5E40]" style={{
      width: compact ? '0.875rem' : '1.125rem',
      height: compact ? '0.875rem' : '1.125rem',
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-2805" data-mp="1vpmduz8lngry" />
    </span>;
}